import { useQuery } from '@apollo/client';
import { ME, GET_EDITOR_PERSON_DATA } from '~/graphql/queries';
import {
  type MeQuery,
  type GetEditorPersonDataQuery,
  type GeneticSex,
  TreeMembershipRole,
} from '~/graphql/gen/graphql';

interface TreeOwner {
  id: number;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
}

interface EditorMembership {
  treeId: number;
  treeName: string | null;
  treeOwner: TreeOwner | null;
}

export interface PersonData {
  personId: number;
  firstName: string | null;
  lastName: string | null;
  geneticSex: GeneticSex | null;
  birthDate: string | null;
  profileImageUrl: string | null;
}

export interface AcceptedInvitationContext {
  isLoading: boolean;
  isEditor: boolean;
  editorMembership: EditorMembership | null;
  treeOwnerName: string;
  treeOwnerProfileImage: string | null;
  treeLastName: string;
  personData: PersonData | null;
}

/**
 * Hook to detect if the current user accepted an invitation and is now an EDITOR
 * of another user's tree. Returns context about the tree owner for personalization.
 *
 * A user is considered to have accepted an invitation if they have an EDITOR
 * membership for a tree they don't own.
 */
export const useAcceptedInvitationContext = (): AcceptedInvitationContext => {
  const { data, loading } = useQuery<MeQuery>(ME);

  const getEditorTreeId = () => {
    if (loading || !data?.me) {
      return null;
    }

    const membership = data.me.memberships?.find(
      (membership) =>
        membership.role === TreeMembershipRole.Editor &&
        membership.tree?.ownerId !== data.me?.id,
    );
    return membership?.treeId ?? null;
  };

  const editorTreeId = getEditorTreeId();

  // Fetch person data from the editor tree
  const { data: personDataResult, loading: personLoading } =
    useQuery<GetEditorPersonDataQuery>(GET_EDITOR_PERSON_DATA, {
      variables: { treeId: editorTreeId },
      skip: !editorTreeId,
    });

  if (loading || personLoading) {
    return {
      isLoading: true,
      isEditor: false,
      editorMembership: null,
      treeOwnerName: '',
      treeOwnerProfileImage: null,
      treeLastName: '',
      personData: null,
    };
  }

  const me = data?.me;
  if (!me) {
    return {
      isLoading: false,
      isEditor: false,
      editorMembership: null,
      treeOwnerName: '',
      treeOwnerProfileImage: null,
      treeLastName: '',
      personData: null,
    };
  }

  // Find EDITOR membership where user is not the owner
  const editorMembership = me.memberships?.find(
    (membership) =>
      membership.role === TreeMembershipRole.Editor &&
      membership.tree?.ownerId !== me.id,
  );

  if (!editorMembership || !editorMembership.tree) {
    return {
      isLoading: false,
      isEditor: false,
      editorMembership: null,
      treeOwnerName: '',
      treeOwnerProfileImage: null,
      treeLastName: '',
      personData: null,
    };
  }

  const tree = editorMembership.tree;
  const owner = tree.owner;
  const ownerFirstName = owner?.firstName ?? '';
  const ownerLastName = owner?.lastName ?? '';
  const treeOwnerName =
    `${ownerFirstName} ${ownerLastName}`.trim() || 'Unknown';

  // Find the person record linked to this user in the editor tree
  const personRecord = personDataResult?.getEditorPersonData;
  const personData: PersonData | null = personRecord
    ? {
        personId: personRecord.id,
        firstName: personRecord.firstName ?? null,
        lastName: personRecord.lastName ?? null,
        geneticSex: personRecord.geneticSex ?? null,
        birthDate: personRecord.birthDate ?? null,
        profileImageUrl: personRecord.profileImageUrl ?? null,
      }
    : null;

  return {
    isLoading: false,
    isEditor: true,
    editorMembership: {
      treeId: editorMembership.treeId,
      treeName: tree.name ?? null,
      treeOwner: owner
        ? {
            id: owner.id,
            firstName: owner.firstName ?? null,
            lastName: owner.lastName ?? null,
            profileImageUrl: owner.profileImageUrl ?? null,
          }
        : null,
    },
    treeOwnerName,
    treeOwnerProfileImage: owner?.profileImageUrl ?? null,
    treeLastName: ownerLastName || 'Unknown',
    personData,
  };
};
